<script lang="ts" module>
	export interface ViewerComponent {
		value(): unknown;
		validate(): Promise<boolean>;
		[key: string]: unknown;
	}
	export const EXTERNAL_ASSET_TYPES = new SvelteSet([AssetType.EXTERNAL_DESTINATION, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.LANDING_PAGE]);
	const REDIRECTING_ASSET_TYPES = new SvelteSet([AssetType.EXTERNAL_DESTINATION, AssetType.SUITE_REF, AssetType.STATIC_ASSET]);
</script>

<script lang="ts">
	import { timestampNow } from "@bufbuild/protobuf/wkt";
	import { getContext, onDestroy, onMount, setContext } from "svelte";
	import { SvelteSet, SvelteURLSearchParams } from "svelte/reactivity";
	import { get, readable, writable, type Readable, type Writable } from "svelte/store";
	import { fade } from "svelte/transition";
	import Geolocation from "svelte-geolocation";
	import { twMerge, type ClassNameValue } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import { AssetType, ConversionType, RenderMode, type AssetRef, type EntryPoint, type Module, type Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { GeoLocationSchema } from "#gen/bundles/v1/common_pb.js";
	import { ConversionMetaDataSchema, InteractionSchema, InteractionType, type ConversionMetaData } from "#gen/bundles/v1/events_pb.js";
	import { ExitRequestSchema, Mode, RequestIntent } from "#gen/bundles/v1/service_pb.js";
	import { AssetLoad } from "#lib/api/bundles.js";
	import type { AssetManager } from "#lib/assetManager.js";
	import { downloadUrl } from "#lib/blob.js";
	import { FcColorBlack } from "#lib/code.js";
	import Badge from "#lib/components/Badge.svelte";
	import StackController from "#lib/components/StackController.svelte";
	import LandingPage from "#lib/components/flow/viewer/LandingPage.svelte";
	import LoadError from "#lib/components/flow/viewer/LoadError.svelte";
	import MoreApps from "#lib/components/flow/viewer/MoreApps.svelte";
	import Navigation from "#lib/components/flow/viewer/Navigation.svelte";
	import PageContainer from "#lib/components/flow/viewer/PageContainer.svelte";
	import PolicyContentViewer from "#lib/components/flow/viewer/PolicyContentViewer.svelte";
	import StatusCard from "#lib/components/flow/viewer/StatusCard.svelte";
	import Tray from "#lib/components/flow/viewer/Tray.svelte";
	import TrayExitButton from "#lib/components/flow/viewer/TrayExitButton.svelte";
	import { firstPageKeys, landsAsFullPage } from "#lib/components/flow/viewer/utils.js";
	import Page from "#lib/components/page/Page.svelte";
	import { parseDagFromSuite, type BundleNode } from "#lib/dag.js";
	import { decideHistoryNav, directLinkDestination, isSubmittedConversion, shouldEmitAbandon } from "#lib/flowNavigation.js";
	import { slidePage } from "#lib/flowTransitions.js";
	import { generateFontStylesheetUrls } from "#lib/fonts.js";
	import { Logger } from "#lib/logging.js";
	import { getExitButtonStyle, presentsAsFullPage } from "#lib/page.js";
	import { createResolutionsStore, indexAssets, withResolution, type ResolutionsStore } from "#lib/passthrough.js";
	import { viewerPath } from "#lib/path.js";
	import { nextPageKeys, pageBackgroundUrl } from "#lib/prefetch.js";
	import { create, toJson, type JsonObject } from "#lib/protobuf-compat.js";
	import { createDataItem } from "#lib/stores/suite.js";
	import { getBackgroundColor, getBrowserChromeColor, getTextColor, hasBackgroundFill, isLight, styleWithBrandKit, type Themeable } from "#lib/theme.js";
	import { PUBLIC_URLS } from "#lib/url.js";
	import { browser } from "$app/env";
	import { goto } from "$app/navigation";
	import { resolve } from "$app/paths";
	import { page } from "$app/state";
	import type { GeolocationCoords } from "svelte-geolocation/Geolocation.svelte";

	interface Props {
		initialAsset?: AssetLoad;
		mode?: Mode;
		interpolate?: boolean;
		readOnly?: boolean;
		mlRequestId?: string;
		isDesktop: boolean;
		preventScroll?: boolean;
		class?: ClassNameValue;
		onLoaded?: (asset: AssetLoad) => void;
		onLateCoords?: (coords: GeolocationCoords) => void;
	}

	const CHROME_DIM_COLOR = FcColorBlack;
	const GEO_MAX_REQUESTS = 3;

	const assetManager: Writable<AssetManager> = getContext("assetManager");
	const entryPoint: Writable<EntryPoint> = getContext("entryPoint");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");
	const suite: Writable<Suite> = getContext("suite");
	const modules: Writable<Record<string, Module>> = getContext("modules");
	const assets: Writable<Record<string, AssetLoad>> = getContext("assets");
	const preventScrollStore: Writable<boolean> | undefined = getContext("preventScroll");
	// The isDesktop prop is already ANDed with desktopViewEnabled by the route, so it cannot tell a
	// phone apart from a letterboxed desktop.
	const isDesktopUserAgent: Readable<boolean | undefined> = getContext<Readable<boolean | undefined> | undefined>("isDesktop") ?? readable(false);
	const passthroughResolutions: ResolutionsStore = getContext<ResolutionsStore | undefined>("passthroughResolutions") ?? createResolutionsStore();
	setContext("passthroughResolutions", passthroughResolutions);
	const pageState = writable({ suite, pageId: "" });
	const options: PositionOptions = {
		timeout: 20_000,
		maximumAge: 60_000,
		enableHighAccuracy: true,
	};

	let { initialAsset, mode = Mode.LIVE, interpolate = false, readOnly = false, mlRequestId, isDesktop, preventScroll = $bindable(false), class: className, onLoaded, onLateCoords }: Props = $props();

	let suiteId = $suite.id;
	let entryPointId = $entryPoint.id;

	let assetKey: string | undefined = $state();
	let coords: GeolocationCoords | undefined = $state();
	let geoComponent: Geolocation | undefined = $state();
	let geoRequests = 1;
	let geoInFlight = true;
	let engaged = false;
	let exitCalled = $state(false);
	let menuOpen = $state(false);
	let trayOpen = $state(false);
	let contentShown = $state(false);
	let moreAppsShown = $state(false);
	const metadata: ConversionMetaData = $state(create(ConversionMetaDataSchema, {}));
	let loadError: unknown = $state();
	let stack: StackController | undefined = $state();
	let converted = $state(false);
	let renderTray = $state(false);
	// The full page shown after navigating to a PAGE_V2 set to "page". Separate from initialAsset (the
	// flow home) so the chain survives reloads and the tray closes back to it.
	let fullPageOverride: AssetLoad | undefined = $state();
	// Bumps only on a genuine full page navigation so the keyed backdrop remounts and slides.
	// Reload, reset and landing change the backdrop without bumping it so they don't slide.
	let navGen = $state(0);
	let navDirection: "forward" | "back" = $state("forward");
	let pageTransitioning = false;
	const submittedKeys: Record<string, true> = {};
	let viewerWidth = $state(0);
	// flowPageIndex mirrors the pushed history index so popstate can tell back from forward.
	// appliedFlowKey is the key last shown, compared to page.state to skip our own pushes.
	// historyReqToken drops a superseded async history load so it can't land late.
	let flowPageIndex = 0;
	let flowPageChainId = newChainId();
	let appliedFlowKey: string | undefined = undefined;
	let historyReqToken = 0;
	// Drops a superseded background reload so overlapping reloads can't land out of order.
	let reloadToken = 0;

	const data = $derived(createDataItem($suite, initialAsset));
	const backdropAsset = $derived(fullPageOverride ?? initialAsset);
	// The backdrop page paints its own background inside the sliding layer, so on a navigation the
	// background slides with the content. The url is tracked only to keep its bytes warm (see below).
	const backdropBgImageUrl = $derived.by(() => {
		const store = backdropAsset?.asPage();
		return store ? pageBackgroundUrl(get(store)) : undefined;
	});
	// Next page background images fetched and decoded ahead of time and kept warm, so a page paints its
	// new background instantly as it slides in. Keyed by url, pruned to the reachable set (see below).
	const decodedBg: Record<string, HTMLImageElement> = {};

	const dag = $derived(parseDagFromSuite($suite, $modules));
	const firstPages = $derived(firstPageKeys(dag));
	const conversions = $derived(dag.findConversionsByAssetKey(initialAsset?.key() ?? ""));
	const apps = $derived(dag.findAppsByAssetKey(initialAsset?.key() ?? "").filter(isModuleEnabled));
	const fontStylesheetUrls = $derived(generateFontStylesheetUrls($brandKit?.fonts));
	const displayTray = $derived(contentShown || moreAppsShown);
	const prefetchSourceKeys = $derived([contentShown && assetKey ? assetKey : undefined, backdropAsset?.key()].filter((key): key is string => Boolean(key)));
	const backdropIsFullPage = $derived(Boolean(backdropAsset && presentsAsFullPage($suite.assets[backdropAsset.key()])));
	const emitsThemeColor = $derived(mode === Mode.LIVE && backdropAsset?.renderMode() !== RenderMode.INLINE && !($isDesktopUserAgent && !$suite?.desktopViewEnabled));

	const chromeColor = $derived(trayOpen || menuOpen ? CHROME_DIM_COLOR : getThemeColor($brandKit, backdropAsset));
	const contentChromeColor = $derived(getBackgroundColor($brandKit, themeableFor(backdropAsset)));
	const trayChromeColor = $derived(getThemeColor($brandKit, assetKey ? $assets[assetKey] : undefined));
	const chromeEdgeColor = $derived({
		top: chromeColor,
		bottom: trayOpen || menuOpen ? (trayOpen && !menuOpen ? trayChromeColor : chromeColor) : contentChromeColor,
	});
	let chromeSampled = $state(false);
	const nextKeys = $derived(browser && mode === Mode.LIVE && backdropIsFullPage ? nextPageKeys(dag, prefetchSourceKeys) : []);

	// A preload can resolve under a different key than the one asked for, leaving that key unfilled
	// so the effect below asks again on every $assets write. Bound each key to one request.
	const preloadRequested: SvelteSet<string> = new SvelteSet();

	// Preload the next pages' data if missing, so their background url exists to warm and the nav is
	// faster. loadAsset already covers a page's own next pages, so this mainly covers the entry hop.
	$effect(() => {
		if (!browser || mode !== Mode.LIVE) {
			return;
		}
		const missing = nextKeys.filter(key => !$assets[key] && !preloadRequested.has(key));
		if (missing.length === 0) {
			return;
		}
		missing.forEach(key => preloadRequested.add(key));
		const gen = passthroughResolutions.generation();

		$assetManager
			.preload($suite, $entryPoint, missing, mode, coords, metadata, RequestIntent.PREFETCH)
			.then(loaded => {
				if (passthroughResolutions.generation() !== gen) {
					missing.forEach(key => preloadRequested.delete(key));
				}
				passthroughResolutions.merge(gen, loaded);
				loaded.forEach(asset => onLoaded?.(asset));
			}) // A failed request wrote nothing, so let those keys be asked for again. A partial response
			// stays marked, retrying a key the server answered around would loop.
			.catch(() => missing.forEach(key => preloadRequested.delete(key)));
	});
	const prefetchImageUrls = $derived.by(() => {
		const urls: SvelteSet<string> = new SvelteSet();
		for (const key of nextKeys) {
			const store = $assets[key]?.asPage();
			if (store) {
				const url = pageBackgroundUrl(get(store));
				if (url) {
					urls.add(url);
				}
			}
		}
		return [...urls];
	});

	// Fetch and decode the next pages' background images ahead of time, at low priority, so a page paints
	// its background instantly as it slides in. Prune the rest to bound memory.
	$effect(() => {
		if (!browser) {
			return;
		}
		const wanted = backdropBgImageUrl ? [...prefetchImageUrls, backdropBgImageUrl] : prefetchImageUrls;
		for (const url of Object.keys(decodedBg)) {
			if (!wanted.includes(url)) {
				delete decodedBg[url];
			}
		}
		for (const url of prefetchImageUrls) {
			if (!decodedBg[url]) {
				const img = new Image();
				img.fetchPriority = "low";
				img.src = url;
				img.decode().catch(() => {});
				decodedBg[url] = img;
			}
		}
	});

	$effect(() => {
		if ($suite.id !== suiteId) {
			reset();
			suiteId = $suite.id;
		}
	});

	$effect(() => {
		if ($entryPoint.id !== entryPointId) {
			if (mode === Mode.PREVIEW) {
				[...apps, ...conversions].forEach(node => $assetManager.invalidate(node.key));
			}
			if (assetKey) {
				const node = dag.getNode(assetKey);
				if (node) {
					show(node.key, node.ref.type, true);
				}
			}
			entryPointId = $entryPoint.id;
		}
	});

	$effect(() => {
		preventScroll = displayTray || menuOpen;
		preventScrollStore?.set(displayTray || menuOpen);
	});

	// Safari on iOS 26 takes its status bar and toolbar colour from the page's own pixels at the top
	// and bottom edges of the layout viewport, and it only re-reads them when an element appears
	// there, not when the page is merely restyled.
	$effect(() => {
		const edges = chromeEdgeColor;
		if (!emitsThemeColor || !needsChromeResample()) {
			return;
		}
		// The colour Safari sampled when the document loaded is already right, so there is nothing to
		// correct until it changes. Skipping the first run keeps the flash off the initial paint.
		if (!chromeSampled) {
			chromeSampled = true;
			return;
		}
		const strips = (["top", "bottom"] as const).map(side => {
			const strip = document.createElement("div");
			strip.setAttribute("aria-hidden", "true");
			strip.style.cssText = `position: fixed; left: 0; right: 0; ${side}: 0; height: 16px; z-index: 2147483000; pointer-events: none;`;
			// Assigned rather than interpolated so a malformed authored colour is rejected by the CSSOM
			// instead of injecting further declarations onto a top layer element.
			strip.style.backgroundColor = edges[side];
			document.body.appendChild(strip);
			return strip;
		});
		function remove() {
			strips.forEach(strip => strip.remove());
		}
		let inner = 0;
		const outer = requestAnimationFrame(() => {
			inner = requestAnimationFrame(remove);
		});
		return () => {
			cancelAnimationFrame(outer);
			cancelAnimationFrame(inner);
			remove();
		};
	});

	$effect(() => {
		if (mode !== Mode.LIVE) {
			return;
		}
		// emitsThemeColor carries the same INLINE and letterboxed desktop terms as the paint guard in
		// Page.svelte, so this covers every case where the backdrop will not repaint the body itself.
		if (emitsThemeColor && backdropAsset?.assetType() === AssetType.PAGE_V2) {
			return;
		}
		document.body.style.removeProperty("background-color");
	});

	onMount(() => {
		metadata.pageloadTimestamp = timestampNow();
	});

	$effect(() => {
		if (mlRequestId) {
			metadata.mlRequestId = mlRequestId;
		}
	});

	setContext("pageState", pageState);

	function newChainId(): string {
		return `${Date.now()}.${Math.random()}`;
	}

	function isModuleEnabled(node: BundleNode<AssetRef>): boolean {
		return !node.ref.disabled;
	}

	export async function refreshAsset(): Promise<AssetLoad | undefined> {
		const key = assetKey ?? fullPageOverride?.key();
		if (key) {
			return await loadAsset(key!);
		}
	}

	function handleInteraction() {
		metadata.interactions.push(
			create(InteractionSchema, {
				interactionType: InteractionType.BUTTON_CLICK,
				timestamp: timestampNow(),
			}),
		);
	}

	async function expandTray() {
		renderTray = true;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				trayOpen = true;
			});
		});
	}

	function collapseTray() {
		trayOpen = false;
	}

	function showMenu() {
		engaged = true;
		menuOpen = true;
	}

	function closeMenu() {
		menuOpen = false;
	}

	let pendingOpen = false;
	let pendingInitialAsset: AssetLoad | undefined;
	// The tray content key shown or loading. A synchronous dedupe marker, separate from assetKey (the
	// render key, set after load) so a click burst dedupes before the tray renders half-loaded content.
	let activeContentKey: string | undefined;
	// A full page swap deferred until an open tray finishes closing in hideContent. Carries push
	// and direction because the swap lands later, when that context is gone.
	let pendingFullPage: { asset: AssetLoad; push: boolean; direction: "forward" | "back"; gated: boolean } | undefined;

	export async function showContent(key: string, assetType: AssetType, animate: boolean = false) {
		if (!key || key === assetKey) {
			return;
		}
		showNode(key, assetType, animate);
	}

	async function handleShowContent(node: BundleNode<AssetRef>) {
		const { key, ref } = node;
		await showNode(key, ref.type);
	}

	async function showNode(key: string, assetType: AssetType, animate: boolean = false) {
		await show(key, assetType, animate);
		handleInteraction();
	}

	function isBackdropOrTrayVisible(asset: AssetLoad): boolean {
		if (asset.assetType() === AssetType.LANDING_PAGE) {
			return initialAsset?.key() === asset.key() && !fullPageOverride;
		}
		return asset.key() === activeContentKey && (trayOpen || contentShown);
	}

	function isAssetDisplayed(asset: AssetLoad): boolean {
		if (landsAsFullPage($suite, firstPages, asset.key())) {
			return fullPageOverride?.key() === asset.key();
		}
		return isBackdropOrTrayVisible(asset);
	}

	function showAsset(asset: AssetLoad, animate: boolean = false, gated: boolean = false) {
		const isFullPage = landsAsFullPage($suite, firstPages, asset.key());
		// Ignore a repeat result for content already on screen (mashing a button) so we don't
		// stack duplicate slide ins. Full pages dedupe in applyFullPage.
		if (!isFullPage && isBackdropOrTrayVisible(asset)) {
			return;
		}
		const shouldPush = trayOpen;
		// A full page asset owns its own tray state in handleAssetLoaded, so don't
		// force the tray open here. toggleTrayIfNecessary side effects contentShown.
		if (!isFullPage) {
			activeContentKey = asset.key();
			toggleTrayIfNecessary(asset.assetType(), animate);
		}
		handleAssetLoaded(asset, shouldPush, gated);
		handleInteraction();
	}

	function showStatus() {
		if (trayOpen) {
			stack?.push(StatusCard, { props: { suite, initialAsset, mode } });
		} else {
			toggleTrayIfNecessary(AssetType.PAGE_V2, true);
		}
	}

	async function show(key: string, assetType: AssetType, animate: boolean = false) {
		engaged = true;
		try {
			if (mode === Mode.LIVE && assetType === AssetType.STATIC_ASSET) {
				handleDownload(key, metadata);
				loadError = undefined;
				return;
			}
			// If the target is preloaded and presents full page, skip the tray gate
			// so it doesn't briefly open before handleAssetLoaded swaps the backdrop.
			if (landsAsFullPage($suite, firstPages, key)) {
				await loadAsset(key, false);
				loadError = undefined;
				return;
			}
			// A goto resolves server side, so skip the gate rather than open a tray we then close.
			// Keep the push flag, a result that only renders in the tray still needs pushing.
			if (dag.getNode(key)?.ref.type === AssetType.GOTO) {
				activeContentKey = key;
				await loadAsset(key, trayOpen);
				loadError = undefined;
				return;
			}
			// Ignore a repeat request for tray content already shown or loading (mashing the open
			// button) so we don't replay the animation or push duplicate pages.
			if (key === activeContentKey && (trayOpen || contentShown)) {
				return;
			}
			const shouldPush = trayOpen;
			// Mark the target before the async load so a burst of clicks in the load gap dedupes.
			// assetKey (the render key) is set later in handleAssetLoaded, once the modules loaded.
			activeContentKey = key;
			toggleTrayIfNecessary(assetType, animate);
			await loadAsset(key, shouldPush);
			loadError = undefined;
		} catch (e) {
			loadError = e;
		}
	}

	function getModuleKeys(key: string): string[] {
		return dag
			.findModulesFromAssetKey(key)
			.filter(node => !node.ref.disabled && node.ref.type !== AssetType.GOTO)
			.map(node => node.key);
	}

	function loadLinkedAssets(asset: AssetLoad) {
		if (mode !== Mode.LIVE || REDIRECTING_ASSET_TYPES.has(asset.assetType()) || isAssetPolicyUrl(asset)) {
			return;
		}
		if (isAssetDisplayed(asset)) {
			return;
		}

		const assetKeys = getModuleKeys(asset.key());
		if (assetKeys.length === 0) {
			return;
		}

		const gen = passthroughResolutions.generation();

		$assetManager
			.preload($suite, $entryPoint, assetKeys, mode, coords, metadata)
			.then(loaded => {
				if (destroyed) {
					return;
				}
				passthroughResolutions.merge(gen, loaded);
				loaded.forEach(item => onLoaded?.(item));
			})
			.catch(err => Logger.error("Failed to load linked assets for current page", { err }));
	}

	async function loadAsset(key: string, shouldPush: boolean = false): Promise<AssetLoad> {
		const assetKeys = getModuleKeys(key);
		const gen = passthroughResolutions.generation();
		const [asset, assets] = await Promise.all([$assetManager.load($suite, $entryPoint, key, mode, coords, metadata), $assetManager.preload($suite, $entryPoint, assetKeys, mode, coords, metadata)]);
		passthroughResolutions.merge(gen, [...assets, asset]);
		assets.forEach(asset => onLoaded?.(asset));
		handleAssetLoaded(asset, shouldPush);
		return asset;
	}

	function toggleTrayIfNecessary(assetType: AssetType, animate: boolean) {
		if (EXTERNAL_ASSET_TYPES.has(assetType)) {
			return;
		}
		if (animate && trayOpen) {
			collapseTray();
			pendingOpen = true;
		} else {
			expandTray();
		}
		contentShown = true;
	}

	// isAssetPolicyUrl checks if an asset is a Policy with the URL type, used
	// to determine if the user should be redirected to the url vs shown the
	// policy content
	function isAssetPolicyUrl(asset: AssetLoad): boolean {
		const policy = asset.asPolicy();
		return policy !== undefined && get(policy).content?.content.case === "url";
	}

	function applyFullPage(asset: AssetLoad | undefined, options: { push?: boolean; direction?: "forward" | "back"; gated?: boolean } = {}) {
		// Ignore a nav to the page already shown (mashing the same button) so we don't stack slides or
		// duplicate history. Reset assetKey, which handleAssetLoaded set before funneling here.
		if (asset?.key() === fullPageOverride?.key()) {
			contentShown = false;
			assetKey = undefined;
			return;
		}
		const { push = false, direction = "forward", gated = false } = options;
		navDirection = direction;
		// Take the current layer out of flow before the incoming one mounts, so the card is never sized
		// by both at once. That transient double height shifts the sticky submit, and overlapOut is too late.
		if (browser && backdropLayer) {
			backdropLayer.classList.add("absolute", "inset-0", direction === "back" ? "z-20" : "z-10");
		}
		navGen += 1;
		beginPageTransition();
		fullPageOverride = asset;
		contentShown = false;
		assetKey = undefined;
		appliedFlowKey = asset?.key();
		if (push && browser && mode === Mode.LIVE) {
			flowPageIndex += 1;

			goto(resolve(page.url.pathname + page.url.search + page.url.hash), {
				shallow: true,
				state: {
					...page.state,
					flowPage: asset
						? {
								key: asset.key(),
								index: flowPageIndex,
								gated,
								chainId: flowPageChainId,
							}
						: undefined,
				},
			});
		}
	}

	// The current backdrop content layer, so a nav can take it out of flow before the next mounts.
	let backdropLayer = $state<HTMLElement | undefined>();
	let transitionTimer: ReturnType<typeof setTimeout> | undefined;
	let bounceTimer: ReturnType<typeof setTimeout> | undefined;
	let reloadTimer: ReturnType<typeof setTimeout> | undefined;
	let swapFrame: number | undefined;
	// A background execute or history load can resolve after the viewer unmounts (a fast route change
	// mid nav). Its continuation must not push history, fire a reload or write shared stores on a dead
	// component, so each awaited path checks this first.
	let destroyed = false;
	onDestroy(() => {
		destroyed = true;
		if (transitionTimer) {
			clearTimeout(transitionTimer);
		}
		if (bounceTimer) {
			clearTimeout(bounceTimer);
		}
		if (reloadTimer) {
			clearTimeout(reloadTimer);
		}
		if (swapFrame) {
			cancelAnimationFrame(swapFrame);
		}
	});

	// Repins the home backdrop after the slide, not during, so reload's wholesale $assets replace can't
	// preempt the transition and pop the new page in. Nothing on the shown page needs the repin at once.
	function scheduleReload() {
		if (reloadTimer) {
			clearTimeout(reloadTimer);
		}
		reloadTimer = setTimeout(() => {
			reloadTimer = undefined;
			reload().catch(err => Logger.error("Failed to reload after execute", { err }));
		}, 300);
	}

	// Suppresses the outgoing page's teardown cancel during a slide. Cleared by a timer, not the
	// transition end event, because onDestroy fires after outroend and would clear it too early.
	function beginPageTransition() {
		if (!browser) {
			return;
		}
		pageTransitioning = true;
		if (transitionTimer) {
			clearTimeout(transitionTimer);
		}
		transitionTimer = setTimeout(() => {
			pageTransitioning = false;
		}, 300);
	}

	function prefersReducedMotion(): boolean {
		return browser && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	}

	// Falls back to window width before the container has measured so the first navigation still
	// slides instead of doing nothing.
	function pageSlideDistance(): number {
		if (prefersReducedMotion()) {
			return 0;
		}
		return viewerWidth || (browser ? window.innerWidth : 0);
	}

	// During a slide the incoming page stays in flow so it sizes the container, while the outgoing goes
	// absolute so the two overlap without stacking height. The newer page sits on top so forward leads
	// with the incoming and back reveals the previous. Guard on currentTarget against nested transitions.
	function overlapIn(e: Event) {
		if (e.target !== e.currentTarget) {
			return;
		}
		(e.currentTarget as HTMLElement).classList.add("relative", navDirection === "back" ? "z-10" : "z-20");
	}

	function overlapOut(e: Event) {
		if (e.target !== e.currentTarget) {
			return;
		}
		(e.currentTarget as HTMLElement).classList.add("absolute", "inset-0", navDirection === "back" ? "z-20" : "z-10");
	}

	function overlapEnd(e: Event) {
		if (e.target !== e.currentTarget) {
			return;
		}
		(e.currentTarget as HTMLElement).classList.remove("relative", "absolute", "inset-0", "z-10", "z-20");
	}

	// Drops any pushed full page entry so back can't resurrect a page the user has left, like
	// returning home or switching suites.
	function clearFlowPageHistory() {
		flowPageIndex = 0;
		flowPageChainId = newChainId();
		appliedFlowKey = undefined;
		if (browser && mode === Mode.LIVE && page.state.flowPage) {
			goto(resolve(page.url.pathname + page.url.search + page.url.hash), {
				shallow: true,
				replace: true,
				state: { ...page.state, flowPage: undefined },
			});
		}
	}

	// Lands a backdrop swap from browser back or forward. A page defers behind an open tray's close
	// so the tray animates out first.
	function navigateFromHistory(asset: AssetLoad | undefined, direction: "forward" | "back") {
		// Landing home has nothing to defer but still needs the close, or an empty tray is stranded.
		const defer = asset !== undefined && trayOpen;
		trayOpen = false;
		if (defer) {
			pendingFullPage = { asset, push: false, direction, gated: false };
			return;
		}
		// A newer back or forward supersedes any nav still deferred behind a closing tray, so drop it
		// or the tray's later hideContent would land the stale page over this one.
		pendingFullPage = undefined;
		applyFullPage(asset, { push: false, direction });
	}

	// Resolves a page key to its asset, reusing the cached load so back and forward don't emit
	// another impression, then lands it unless a newer popstate won.
	async function loadFromHistory(key: string, direction: "forward" | "back", token: number) {
		try {
			const asset = $assets[key] ?? (await $assetManager.load($suite, $entryPoint, key, mode, coords, metadata));
			if (destroyed || token !== historyReqToken) {
				return;
			}
			navigateFromHistory(asset, direction);
			loadError = undefined;
		} catch (e) {
			// Only surface the error if this is still the active request. A superseded load that
			// rejects must not overwrite a newer page with a stale error.
			if (token === historyReqToken) {
				loadError = e;
			}
		}
	}

	// Reacts to browser back and forward, applying the pure decideHistoryNav decision.
	// The insync case skips the echo from our own pushState so it never loops.
	$effect(() => {
		if (!browser || mode !== Mode.LIVE) {
			return;
		}
		const decision = decideHistoryNav(page.state.flowPage, flowPageIndex, appliedFlowKey, flowPageChainId);
		if (decision.action === "insync") {
			flowPageIndex = decision.index;
			return;
		}
		// Defer the bounce to a macrotask because history.back() during popstate is unreliable and can
		// strand the pointer, needing an extra Back. Guard on key and index since index is reused after
		// a history clear, and replace any pending bounce so timers can't stack.
		if (decision.action === "bounce") {
			const { key: gatedKey, index: gatedIndex } = decision;
			if (bounceTimer) {
				clearTimeout(bounceTimer);
			}
			bounceTimer = setTimeout(() => {
				bounceTimer = undefined;
				if (browser && page.state.flowPage?.key === gatedKey && page.state.flowPage?.index === gatedIndex) {
					history.back();
				}
			}, 0);
			return;
		}
		flowPageIndex = decision.index;
		appliedFlowKey = decision.action === "load" ? decision.key : undefined;
		const token = ++historyReqToken;
		if (decision.action === "home") {
			loadError = undefined;
			navigateFromHistory(undefined, decision.direction);
		} else if (landsAsFullPage($suite, firstPages, decision.key)) {
			loadFromHistory(decision.key, decision.direction, token);
		}
	});

	function handleAssetLoaded(asset: AssetLoad, shouldPush: boolean = false, gated: boolean = false) {
		assetKey = asset.key();
		loaded(asset);
		if (mode === Mode.EDITING) return;
		if (asset.asExit()) {
			showStatus();
		} else if (asset.asLandingPage()) {
			if (trayOpen) {
				pendingInitialAsset = asset;
				trayOpen = false;
			} else {
				initialAsset = asset;
				fullPageOverride = undefined;
				clearFlowPageHistory();
			}
		} else if (landsAsFullPage($suite, firstPages, asset.key())) {
			if (trayOpen) {
				// Defer the swap until the tray finishes collapsing (hideContent).
				pendingFullPage = { asset, push: true, direction: "forward", gated };
				trayOpen = false;
			} else {
				applyFullPage(asset, { push: true, gated });
			}
		} else if (asset.asExternalDestination()) {
			const external = get(asset.asExternalDestination()!);
			handleRedirect(external.resolvedUrl);
		} else if (asset.asStaticAsset()) {
			handleDownload(asset.key(), metadata);
		} else if (asset.asSuiteRef()) {
			handleSuiteRef(asset);
		} else if (isAssetPolicyUrl(asset)) {
			handlePolicyUrl(asset);
		} else if (shouldPush) {
			if (asset.asPage()) {
				stack?.push(Page, { props: { mode, asset, onExecute: execute, onReload: refreshAsset, modal: true, fullBleedContainer: true } });
			} else if (asset.asPageContainer()) {
				stack?.push(PageContainer, { props: { mode, dag, metadata, asset, themeStyle: getThemeStyle($brandKit, asset), isLightBackground: isLightColor($brandKit, asset), onExecute: execute, readOnly, onCancel: cancel, onShow: handleShowContent, modal: true } });
			} else if (asset.asPolicy()) {
				stack?.push(PolicyContentViewer, { props: { asset, metadata } });
			}
		} else {
			showContent(asset.key(), asset.assetType(), true);
		}
	}

	function loaded(asset: AssetLoad) {
		$pageState = { ...$pageState, pageId: asset.key() };
		onLoaded?.(asset);
	}

	function reset() {
		collapseTray();
		// Drop deferred swaps before hideContent so it can't complete one into a push during reset.
		pendingFullPage = undefined;
		pendingInitialAsset = undefined;
		preloadRequested.clear();
		hideContent();
		fullPageOverride = undefined;
		clearFlowPageHistory();
	}

	export function dismissContent() {
		collapseTray();
	}

	async function handleShowMoreApps() {
		engaged = true;
		expandTray();
		handleInteraction();
		moreAppsShown = true;
	}

	function hideContent() {
		renderTray = false;
		moreAppsShown = false;
		if (pendingOpen) {
			pendingOpen = false;
			expandTray();
			return;
		}
		contentShown = false;
		assetKey = undefined;
		activeContentKey = undefined;
		converted = false;
		if (pendingInitialAsset) {
			initialAsset = pendingInitialAsset;
			pendingInitialAsset = undefined;
			fullPageOverride = undefined;
			clearFlowPageHistory();
		}
		if (pendingFullPage) {
			const pending = pendingFullPage;
			pendingFullPage = undefined;
			applyFullPage(pending.asset, { push: pending.push, direction: pending.direction, gated: pending.gated });
		}
	}

	async function loadSuite(suiteId: string) {
		closeMenu();
		if ($suite.id === suiteId) {
			return;
		}
		if (mode === Mode.LIVE) {
			reset();
			await goto(resolve(viewerPath(`${suiteId}/${$entryPoint.id}`)));
		}
	}

	async function showShare() {
		if (!navigator.share) {
			return;
		}
		const shareData: ShareData = {
			title: data.primaryText,
			url: window.location.href,
		};
		try {
			await navigator.share(shareData);
		} catch (err) {
			Logger.error("Failed to share", { err });
		}
	}

	function handleRedirect(url: string) {
		setTimeout(() => {
			window.open(url, "_self");
		});
		collapseTray();
	}

	// Starts a file download. WebKit only honours a download that begins on the gesture stack, so this
	// has to be called straight from the click, never from a promise continuation or a timer.
	function handleDownload(assetKey: string, metadata: ConversionMetaData) {
		const context = new SvelteURLSearchParams();
		if (metadata.mlRequestId) {
			context.append("mlRequestId", metadata.mlRequestId);
		}
		if (metadata.parentMlRequestId) {
			context.append("parentMlRequestId", metadata.parentMlRequestId);
		}
		if (coords) {
			context.append("coords", JSON.stringify(coords));
		}
		const params = context.toString();
		collapseTray();
		// An anchor click rather than window.open. A popup is blocked outright without activation, and
		// navigating the tab fires beforeunload and flashes the page before the attachment cancels it.
		downloadUrl(`/dl/${$suite.id}/${$entryPoint.key}/${$entryPoint.id}/${assetKey}${params.length > 0 ? "?" + params : ""}`);
	}

	async function handleSuiteRef(asset: AssetLoad) {
		const store = asset.asSuiteRef();
		if (store) {
			const ref = get(store);
			await goto(resolve(`s/${ref.suiteId}/${ref.entryPoint}/${$entryPoint.id}`));
		}
	}

	function handlePolicyUrl(asset: AssetLoad) {
		const store = asset.asPolicy();
		if (store) {
			const policy = get(store);
			if (policy.content?.content.case == "url") {
				handleRedirect(policy.content.content.value);
			}
		}
	}

	// The already preloaded full page a plain nav button points straight at, or undefined so the
	// caller awaits the server instead of guessing. directLinkDestination applies the link guards
	// (single, unconditional, enabled). Here we add that it presents full page and is preloaded.
	function directFullPageDestination(key: string, conversionComponent: string | undefined): AssetLoad | undefined {
		const destination = directLinkDestination(dag, key, conversionComponent);
		if (!destination) {
			return undefined;
		}
		return presentsAsFullPage($suite.assets[destination]) ? $assets[destination] : undefined;
	}

	// The static asset a plain nav anchor points straight at, so the download can start from the click
	// instead of after the server round trip. Same link guards as directFullPageDestination.
	function directDownloadDestination(key: string, conversionComponent: string | undefined): string | undefined {
		const destination = directLinkDestination(dag, key, conversionComponent);
		if (!destination) {
			return undefined;
		}
		return $suite.assets[destination]?.type === AssetType.STATIC_ASSET ? destination : undefined;
	}

	// Records the nav and repins home after an optimistic slide, off the critical path. If the server
	// resolves a different page than predicted (a direct link never does), it lands that instead.
	function completeNavInBackground(asset: AssetLoad, conversionType: ConversionType, conversionComponent: string, sourceAnchor: string | undefined, value: JsonObject, metadata: ConversionMetaData, predictedKey: string) {
		$assetManager
			.execute($suite, $entryPoint, asset.key(), conversionType, conversionComponent, sourceAnchor, coords, value, metadata, mode)
			.then(res => {
				if (destroyed) {
					return;
				}
				converted = true;
				// Correct only while still on the predicted page. A direct link never resolves elsewhere,
				// but if it somehow did and the user already navigated away, don't yank them back.
				const onPredicted = fullPageOverride?.key() === predictedKey;
				if (onPredicted && typeof res === "string") {
					handleRedirect(res);
				} else if (onPredicted && res instanceof AssetLoad && res.key() !== predictedKey) {
					loadLinkedAssets(res);
					showAsset(res, false, false);
				}
				scheduleReload();
			})
			.catch(err => Logger.error("Failed to execute page", { err }));
	}

	// Runs the swap on the next animation frame. Safari treats a slide set up straight from an async
	// continuation as already finished, so the page just appears. A frame boundary lets the intro
	// play, matching the synchronous nav path. Chrome plays it either way.
	function slideInNextFrame(asset: AssetLoad, gated: boolean) {
		if (!browser) {
			loadLinkedAssets(asset);
			showAsset(asset, false, gated);
			return;
		}
		// Cancel any pending frame so two fast navs can't both swap, doubling the slide and history push.
		if (swapFrame) {
			cancelAnimationFrame(swapFrame);
		}
		swapFrame = requestAnimationFrame(() => {
			swapFrame = undefined;
			loadLinkedAssets(asset);
			showAsset(asset, false, gated);
		});
	}

	async function execute(asset: AssetLoad, conversionType: ConversionType, conversionComponent: string, sourceAnchor: string | undefined, value: JsonObject, metadata: ConversionMetaData): Promise<JsonObject | undefined> {
		engaged = true;
		// Gate a forward only when the result came from submitting a conversion, not from a plain nav
		// button on a page that also has a form (see isSubmittedConversion). conversionComponent is the
		// id of the component the user triggered, matched against the page's own conversion component.
		const submittedConversion = isSubmittedConversion(conversionComponent, asset.conversionComponent());
		// A download has to start on the gesture stack (see handleDownload) and the awaits below put it
		// roughly a second away, so a plain nav button pointing straight at a static asset downloads now
		// and records the conversion in the background. A submitted conversion or a conditional link
		// still has to wait for the server to resolve. LIVE only, so editor preview is unchanged.
		const download = mode === Mode.LIVE && !submittedConversion ? directDownloadDestination(asset.key(), conversionComponent) : undefined;
		if (download) {
			converted = true;
			handleDownload(download, metadata);
			completeNavInBackground(asset, conversionType, conversionComponent, sourceAnchor, value, metadata, download);
			return undefined;
		}
		// A plain nav button to an already preloaded full page slides at once, recording the nav in the
		// background, so a forward click feels as instant as back. Anything server resolved (conversions,
		// branches, an open tray) still waits. LIVE only so editor preview keeps the ordered behaviour.
		const predicted = mode === Mode.LIVE && !submittedConversion && !trayOpen ? directFullPageDestination(asset.key(), conversionComponent) : undefined;
		if (predicted) {
			// Mark converted before the swap, matching the awaited branch, so the outgoing page's
			// teardown never emits a stray cancel while the background execute is still in flight.
			converted = true;
			loadLinkedAssets(predicted);
			showAsset(predicted, false, false);
			completeNavInBackground(asset, conversionType, conversionComponent, sourceAnchor, value, metadata, predicted.key());
			return undefined;
		}
		// Recorded before the round trip: a submit torn down while it is still in flight must not be
		// reported as an abandon, and the catch below undoes it when the submit actually failed.
		const gated = submittedConversion && asset.conversionType() !== ConversionType.UNSPECIFIED;
		if (gated) {
			submittedKeys[asset.key()] = true;
		}
		try {
			const res = await $assetManager.execute($suite, $entryPoint, asset.key(), conversionType, conversionComponent, sourceAnchor, coords, value, metadata, mode);
			const executeResult = res instanceof AssetLoad ? get(res.result()) : undefined;
			if (destroyed) {
				return executeResult;
			}
			const leavingForFlow = mode === Mode.LIVE && res instanceof AssetLoad && res.assetType() === AssetType.SUITE_REF;
			// A forward that lands on a full page slides right away and repins home in the background, so
			// it feels instant. LIVE only. Other results keep the original order (home settled first).
			if (mode === Mode.LIVE && !leavingForFlow && res instanceof AssetLoad && res.assetType() !== AssetType.UNSPECIFIED && landsAsFullPage($suite, firstPages, res.key())) {
				converted = true;
				slideInNextFrame(res, gated);
				scheduleReload();
				return executeResult;
			}
			if (leavingForFlow) {
				converted = true;
			} else {
				await reload();
				converted = true;
			}
			if (!res) {
				trayOpen = false;
			} else if (typeof res === "string") {
				handleRedirect(res as string);
			} else if (res instanceof AssetLoad) {
				if (res.assetType() === AssetType.UNSPECIFIED) {
					trayOpen = false;
				} else {
					loadLinkedAssets(res);
					showAsset(res as AssetLoad, false, gated);
				}
			}
			return executeResult;
		} catch (err) {
			if (gated) {
				delete submittedKeys[asset.key()];
			}
			Logger.error("Failed to execute page", { err });
			throw err;
		}
	}

	function cancel(asset: AssetLoad, metadata: ConversionMetaData) {
		// A backdrop slide tears down the outgoing page. That teardown is not a user dismissal so
		// don't emit a cancel for it.
		if (pageTransitioning) {
			return;
		}
		if (asset && shouldEmitAbandon(mode, asset.conversionType(), submittedKeys[asset.key()] === true)) {
			try {
				$assetManager.cancel($suite, $entryPoint, asset.key(), coords, metadata, mode);
			} catch (err) {
				Logger.error("Failed to emit cancel event", { err });
			}
		}
	}

	function sendExitBeacon(suite: Suite, entryPoint: EntryPoint, key: string, coords?: GeolocationCoords, metadata?: ConversionMetaData, mode: Mode = Mode.LIVE): boolean {
		if (browser && navigator.sendBeacon) {
			const url = `${PUBLIC_URLS.bundles || ""}/bundles.v1.BundleService/Exit`;
			const geolocation = create(GeoLocationSchema, {
				longitude: Number(coords ? coords[0] : -1),
				latitude: Number(coords ? coords[1] : -1),
			});
			const request = create(ExitRequestSchema, {
				suiteId: suite.id,
				version: suite.version,
				key,
				entryPoint,
				metadata,
				mode,
				geolocation,
			});
			const payload = toJson(ExitRequestSchema, request);
			const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
			return navigator.sendBeacon(url, blob);
		} else {
			return false;
		}
	}

	function exit() {
		if (!exitCalled && initialAsset && mode === Mode.LIVE) {
			exitCalled = true;
			if (!sendExitBeacon($suite, $entryPoint, initialAsset.key(), coords, metadata, mode)) {
				Logger.error("Failed to queue exit event");
			}
		}
	}

	function hasEngaged(): boolean {
		return engaged || navGen > 0 || converted || fullPageOverride !== undefined || trayOpen || contentShown;
	}

	export function canLandGeoRecovery(): boolean {
		return !destroyed && !hasEngaged();
	}

	function handlePosition(event: CustomEvent<Partial<GeolocationPosition> | undefined>) {
		geoInFlight = false;
		if (destroyed) {
			return;
		}
		const position = event.detail?.coords;
		if (!position) {
			return;
		}
		coords = [position.longitude, position.latitude];
		if (canLandGeoRecovery()) {
			onLateCoords?.(coords);
		}
	}

	function handleGeoError() {
		geoInFlight = false;
	}

	function requestGeolocation() {
		if (!geoComponent || coords || geoInFlight || geoRequests >= GEO_MAX_REQUESTS) {
			return;
		}
		geoRequests += 1;
		geoInFlight = true;
		void geoComponent.getGeolocationPosition(options);
	}

	function handleVisibilityChange() {
		if (!browser) {
			return;
		}
		if (document.visibilityState === "hidden") {
			exit();
		} else {
			// Tab became visible again - reset flag so we can track next exit
			exitCalled = false;
			requestGeolocation();
		}
	}

	function themeableFor(asset?: AssetLoad): Themeable | undefined {
		if (asset?.assetType() === AssetType.LANDING_PAGE && asset.asLandingPage()) {
			return get(asset.asLandingPage()!);
		} else if (asset?.assetType() === AssetType.PAGE_CONTAINER && asset.asPageContainer()) {
			return get(asset.asPageContainer()!);
		} else if (asset?.assetType() === AssetType.PAGE_V2 && asset.asPage()) {
			return get(asset.asPage()!);
		}
		return undefined;
	}

	function getThemeStyle(brandKit: BrandKit | undefined, asset?: AssetLoad): string {
		return styleWithBrandKit(brandKit, themeableFor(asset));
	}

	function isLightColor(brandKit: BrandKit | undefined, asset?: AssetLoad): boolean {
		return isLight(getBackgroundColor(brandKit, themeableFor(asset)));
	}

	// A page container renders the Blank layout whatever layoutName it stores, that name only picks
	// the app switcher, so key the chrome on what actually paints rather than on the stored value.
	function needsChromeResample(): boolean {
		if (!browser) {
			return false;
		}
		const userAgent = navigator.userAgent;
		return /\b(iPhone|iPad|iPod)\b/.test(userAgent) || (/\bMacintosh\b/.test(userAgent) && navigator.maxTouchPoints > 1);
	}

	function getThemeColor(brandKit: BrandKit | undefined, asset?: AssetLoad): string {
		const renderedLayout = asset?.assetType() === AssetType.PAGE_CONTAINER ? "blank" : undefined;
		return getBrowserChromeColor(brandKit, themeableFor(asset), renderedLayout);
	}

	async function reload() {
		// Now that reload can run in the background off a nav, two can overlap. Drop a superseded one
		// so an out of order landing can't clobber home state with a stale snapshot.
		const token = ++reloadToken;
		const nodes = dag.bfs().filter(node => !node.ref.disabled && node.ref.type !== AssetType.GOTO);
		const keys = nodes.map(node => node.key);
		const refreshMode = mode === Mode.LIVE ? Mode.RELOAD : mode;
		const [asset, loaded] = await Promise.all([$assetManager.execute($suite, $entryPoint, $entryPoint.key, ConversionType.UNSPECIFIED, "", "", coords, undefined, metadata, refreshMode, RequestIntent.REFRESH), $assetManager.preload($suite, $entryPoint, keys, mode, coords, metadata, RequestIntent.PREFETCH)]);
		if (destroyed || token !== reloadToken) {
			return;
		}
		const indexed = indexAssets(loaded, item => item);
		if (asset instanceof AssetLoad) {
			initialAsset = asset;
		} else {
			initialAsset = undefined;
		}
		$assets = indexed.assets;
		passthroughResolutions.replace(asset instanceof AssetLoad ? withResolution(indexed.resolutions, asset) : indexed.resolutions);
	}
</script>

<svelte:window onvisibilitychange={handleVisibilityChange} onbeforeunload={exit} onpagehide={exit} oninput={() => (engaged = true)} />

<svelte:head>
	{#if emitsThemeColor}
		<meta name="theme-color" content={chromeColor} />
	{/if}
	{#if fontStylesheetUrls.length > 0}
		<link rel="preconnect" href="https://fonts.googleapis.com" />
		<link rel="preconnect" href="https://fonts.gstatic.com" />
		{#each fontStylesheetUrls as href (href)}
			<link rel="preload" {href} as="style" />
			<link rel="stylesheet" {href} />
		{/each}
	{/if}
</svelte:head>

{#snippet body(asset: AssetLoad | undefined, modal?: boolean, fullBleedContainer?: boolean)}
	{@const themeStyle = getThemeStyle($brandKit, asset)}
	{@const isLightBackground = isLightColor($brandKit, asset)}
	{#if asset?.assetType() === AssetType.PAGE_CONTAINER}
		<PageContainer {asset} {dag} {metadata} {mode} {interpolate} {readOnly} {themeStyle} {isLightBackground} onShow={handleShowContent} onMore={handleShowMoreApps} onExecute={execute} onCancel={cancel} {modal} />
	{:else if asset?.assetType() === AssetType.LANDING_PAGE}
		<LandingPage {asset} {dag} {metadata} {mode} {interpolate} {readOnly} {themeStyle} {isLightBackground} onShow={handleShowContent} onMore={handleShowMoreApps} onMenu={showMenu} onShare={showShare} onExecute={execute} onCancel={cancel} {isDesktop} {modal} canConvert={data.canConvert} />
	{:else if asset?.assetType() === AssetType.PAGE_V2}
		<Page {asset} parentMlRequestId={mlRequestId} renderMode={asset?.renderMode()} {themeStyle} {mode} {interpolate} onExecute={execute} onReload={refreshAsset} onCancel={cancel} {modal} {fullBleedContainer} />
	{:else if asset?.assetType() === AssetType.POLICY}
		<PolicyContentViewer {asset} {metadata} onCancel={cancel} />
	{/if}
{/snippet}

{#snippet tray()}
	{@const trayAsset = assetKey ? $assets[assetKey] : undefined}
	{@const themeStyle = getThemeStyle($brandKit, trayAsset)}
	{@const isLightBackground = isLightColor($brandKit, trayAsset)}
	{@const trayIsPage = trayAsset?.assetType() === AssetType.PAGE_V2}
	{@const trayPageStore = trayIsPage ? trayAsset?.asPage() : undefined}
	{@const trayNode = assetKey ? dag.getNode(assetKey) : undefined}
	{@const trayIsNested = !trayNode || Boolean(trayNode.parent)}
	{@const exitStyle = getExitButtonStyle(trayIsNested || !trayPageStore ? undefined : get(trayPageStore).config)}
	{@const trayFrameContent = trayIsPage && hasBackgroundFill($brandKit, trayPageStore ? get(trayPageStore) : undefined)}

	<Tray bind:open={trayOpen} onHide={hideContent} {isDesktop} style={themeStyle} fullBleed={trayIsPage} frameContent={trayFrameContent}>
		{#snippet content(close)}
			{#if trayIsPage}
				<TrayExitButton style={exitStyle} onClose={close} />
			{/if}
			<StackController bind:this={stack} closable={!trayIsPage} onClose={close} back={false} bare={trayIsPage} titleClass={isLightBackground ? "text-gray-900" : "text-white"}>
				<div in:fade class="flex h-full min-h-0 flex-col">
					{#if contentShown}
						{#if assetKey}
							{#if !loadError}
								{@render body($assets[assetKey], true, trayIsPage)}
							{:else}
								<LoadError error={loadError} {mode} />
							{/if}
						{:else if converted}
							<StatusCard {suite} {initialAsset} {mode} />
						{/if}
					{:else if moreAppsShown}
						<MoreApps {apps} onShow={handleShowContent} />
					{/if}
				</div>
				{#snippet left()}
					{#if assetKey && $assets[assetKey]?.key() === dag.conversions[0]?.key && data.highlightText}
						{@const color = data.highlightText?.colorHex || "#374151"}
						<Badge rounded class="px-3 py-1.5 text-sm" style="background-color: {color}; color: {getTextColor(color)};">
							{data.highlightText.text}
						</Badge>
					{/if}
				{/snippet}
			</StackController>
		{/snippet}
	</Tray>
{/snippet}

{#if $suite.advancedGeoEnabled && mode === Mode.LIVE}
	<Geolocation bind:this={geoComponent} getPosition on:position={handlePosition} on:error={handleGeoError} {options} />
{/if}
<Navigation bind:open={menuOpen} onLoad={loadSuite} />
<div bind:clientWidth={viewerWidth} class={twMerge("suite-viewer grow", className)} style={getThemeStyle($brandKit, backdropAsset)}>
	{#if trayOpen || menuOpen}
		<div transition:fade={{ duration: 100 }} class="absolute inset-0 z-1 bg-black/60 bg-linear-to-t from-transparent from-85% to-black"></div>
	{/if}
	<!-- isolate scopes the sliding layers' z-index below the dim overlay, and relative gives the outgoing
	layer (which goes absolute mid-slide, see overlapOut) its positioning context. Flex, not grid, so the
	card sizes to the page like a plain load. The page paints its own background, so on a navigation the
	background slides with the content. The suite-viewer stays unpositioned so the tray and the dim overlay
	anchor to the flow viewport box in the consumer layout, not to this content-height card. -->
	<div class="relative isolate flex h-full w-full grow flex-col">
		{#key navGen}
			{@const dist = pageSlideDistance()}
			{@const dur = prefersReducedMotion() ? 0 : 250}
			<div bind:this={backdropLayer} class="flex h-full w-full grow flex-col" in:slidePage={{ x: navDirection === "forward" ? dist : -dist, duration: dur }} out:slidePage={{ x: navDirection === "forward" ? -dist : dist, duration: dur }} onintrostart={overlapIn} onoutrostart={overlapOut} onintroend={overlapEnd} onoutroend={overlapEnd}>
				{@render body(backdropAsset)}
			</div>
		{/key}
	</div>
	{#if renderTray}
		{@render tray()}
	{/if}
</div>
